interface PaginatedData<T> {
  list?: T[] | null;
  totalCount?: number | null;
}

interface ApiResponseWithData<T> {
  data?: PaginatedData<T>;
}

export type ApiErrorBody = {
  message?: string;
  Message?: string;
  errors?: string[] | Record<string, string[] | string>;
  error?: string;
};

function normalizeApiErrors(
  errors: ApiErrorBody['errors'],
): string[] {
  if (!errors) return [];

  if (Array.isArray(errors)) {
    return errors.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }

  if (typeof errors === 'object') {
    return Object.values(errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }

  return [];
}

/** Builds a toast-ready message from API body, appending `errors` when present. */
export function formatApiErrorMessage(
  body: ApiErrorBody | null | undefined,
  fallback = 'Si è verificato un errore imprevisto.',
): string {
  const message =
    (typeof body?.message === 'string' && body.message.trim()) ||
    (typeof body?.Message === 'string' && body.Message.trim()) ||
    fallback;

  const details = normalizeApiErrors(body?.errors);
  if (!details.length) {
    return message;
  }

  return `${message}<br>${details.join('<br>')}`;
}

export function mapPaginatedListResponse<T>(
  res: ApiResponseWithData<T>,
): { items: T[]; total: number } {
  return {
    items: res?.data?.list ?? [],
    total: res?.data?.totalCount ?? 0,
  };
}

export function mapPaginatedListResponseOr<T>(
  res: ApiResponseWithData<T>,
): { items: T[]; total: number } {
  return {
    items: res.data?.list ?? [],
    total: res.data?.totalCount ?? 0,
  };
}

export function extractListOrEmpty<T>(res: { data?: { list?: T[] } }): T[] {
  return res.data?.list || [];
}
